import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Pencil } from 'lucide-react';

import { api } from '../lib/api.ts';
import { CARGOS, data as formatarData, mesAnterior, reais } from '../lib/formato.ts';
import { Aviso, Botao, Campo, Carregando, Cartao, Etiqueta, Tabela } from '../componentes/ui.tsx';

interface SalarioDeFuncionario {
  funcionarioId: number;
  nome: string;
  cargoCodigo: string | null;
  salario: number | null;
  vigenciaInicio: string | null;
  observacao: string | null;
  atualizadoPor: string | null;
}

interface Lista {
  competencia: string;
  data: string;
  /** Vem da regra vigente. A tela não fixa 30. */
  divisorDoSalario: number;
  total: number;
  semSalario: number;
  funcionarios: SalarioDeFuncionario[];
}

interface ItemDoHistorico {
  id: string;
  salario: number;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  observacao: string | null;
  criado_por: string | null;
}

export function Salarios() {
  const cache = useQueryClient();
  const [competencia, setCompetencia] = useState(mesAnterior().inicio.slice(0, 7));
  const [editando, setEditando] = useState<SalarioDeFuncionario | null>(null);
  const [verHistorico, setVerHistorico] = useState<SalarioDeFuncionario | null>(null);

  const [valor, setValor] = useState('');
  const [inicio, setInicio] = useState('');
  const [observacao, setObservacao] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['salarios', competencia],
    queryFn: () => api<Lista>(`/salarios?competencia=${competencia}`),
  });

  const historico = useQuery({
    queryKey: ['salario-historico', verHistorico?.funcionarioId],
    queryFn: () =>
      api<{ historico: ItemDoHistorico[] }>(`/salarios/${verHistorico?.funcionarioId}/historico`),
    enabled: verHistorico !== null,
  });

  const salvar = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/salarios', {
        metodo: 'PUT',
        corpo: {
          funcionarioId: editando?.funcionarioId,
          nome: editando?.nome,
          salario: Number(valor.replace(/\./g, '').replace(',', '.')),
          vigenciaInicio: inicio,
          observacao,
        },
      }),
    onSuccess: () => {
      setEditando(null);
      void cache.invalidateQueries({ queryKey: ['salarios'] });
      void cache.invalidateQueries({ queryKey: ['salario-historico'] });
    },
  });

  function abrirEdicao(f: SalarioDeFuncionario) {
    setEditando(f);
    setValor(f.salario === null ? '' : String(f.salario).replace('.', ','));
    // Uma vigência nova precisa começar depois da anterior. O primeiro dia da
    // competência é o padrão que quase sempre é o certo.
    setInicio(`${competencia}-01`);
    setObservacao('');
    salvar.reset();
  }

  if (isLoading) return <Carregando texto="Consultando a réplica do Feegow…" />;
  if (isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar">
        <p>{error.message}</p>
      </Aviso>
    );
  }
  if (data === undefined) return null;

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Salários"
        descricao="O valor do dia sai daqui e é o que cada falta justificada desconta da premiação. Alterar não sobrescreve: abre uma vigência nova, para que mês já fechado continue usando o salário da época."
        acoes={
          <div className="w-52">
            <Campo rotulo="Competência" tipo="month" valor={competencia} onChange={setCompetencia} />
          </div>
        }
      >
        <div className="space-y-4">
          {data.semSalario > 0 && (
            <Aviso titulo={`${data.semSalario} pessoa(s) sem salário cadastrado`}>
              <p>
                Faltas justificadas dessas pessoas <strong>não serão descontadas</strong> — o
                fechamento avisa em vez de descontar um valor errado.
              </p>
            </Aviso>
          )}

          {salvar.isError && (
            <Aviso tom="erro">
              <p>{salvar.error.message}</p>
            </Aviso>
          )}

          <Tabela
            cabecalho={
              <tr>
                <th className="py-2 pr-3">Funcionário</th>
                <th className="py-2 pr-3">Cargo</th>
                <th className="py-2 pr-3 text-right">Salário</th>
                <th className="py-2 pr-3 text-right">Valor do dia</th>
                <th className="py-2 pr-3">Desde</th>
                <th className="py-2 pr-3">Observação</th>
                <th className="py-2" />
              </tr>
            }
          >
            {data.funcionarios.map((f) => (
              <tr key={f.funcionarioId} className="hover:bg-slate-50">
                <td className="py-2 pr-3">{f.nome}</td>
                <td className="py-2 pr-3 text-slate-600">
                  {CARGOS[f.cargoCodigo ?? ''] ?? f.cargoCodigo}
                </td>
                <td className="num py-2 pr-3">
                  {f.salario === null ? (
                    <Etiqueta tom="ruim">sem cadastro</Etiqueta>
                  ) : (
                    reais(f.salario)
                  )}
                </td>
                <td className="num py-2 pr-3 text-slate-500">
                  {f.salario === null ? '—' : reais(f.salario / data.divisorDoSalario)}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                  {formatarData(f.vigenciaInicio)}
                </td>
                <td className="max-w-72 py-2 pr-3 text-xs text-slate-500">{f.observacao ?? '—'}</td>
                <td className="py-2">
                  <div className="flex gap-1">
                    <button
                      onClick={() => abrirEdicao(f)}
                      title={f.salario === null ? 'Cadastrar salário' : 'Alterar salário'}
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
                    >
                      <Pencil className="size-4" />
                    </button>
                    <button
                      onClick={() => setVerHistorico(f)}
                      title="Histórico de salários"
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
                    >
                      <History className="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Tabela>
        </div>
      </Cartao>

      {editando !== null && (
        <Cartao titulo={`Salário de ${editando.nome}`}>
          <div className="max-w-xl space-y-4">
            {editando.salario !== null && (
              <Aviso tom="info">
                <p>
                  O salário atual é {reais(editando.salario)} desde{' '}
                  {formatarData(editando.vigenciaInicio)}. Ele será encerrado no dia anterior à nova
                  vigência, e continua valendo para os fechamentos daquele período.
                </p>
              </Aviso>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Salário mensal"
                valor={valor}
                onChange={setValor}
                dica={`Só o número, com vírgula nos centavos. O valor do dia é o salário ÷ ${data.divisorDoSalario}.`}
                obrigatorio
              />
              <Campo
                rotulo="Vigente a partir de"
                tipo="date"
                valor={inicio}
                onChange={setInicio}
                obrigatorio
              />
            </div>

            <Campo
              rotulo="Observação"
              valor={observacao}
              onChange={setObservacao}
              dica="Ex.: promoção, dissídio, correção. Aparece no histórico e no log."
            />

            {valor.trim() !== '' && (
              <p className="text-sm text-slate-600">
                Valor do dia:{' '}
                <strong className="font-mono">
                  {reais(Number(valor.replace(/\./g, '').replace(',', '.')) / data.divisorDoSalario)}
                </strong>{' '}
                — é o que cada falta justificada desconta.
              </p>
            )}

            <div className="flex gap-3">
              <Botao
                onClick={() => salvar.mutate()}
                carregando={salvar.isPending}
                desabilitado={valor.trim() === '' || inicio === ''}
              >
                Salvar
              </Botao>
              <Botao tipo="secundario" onClick={() => setEditando(null)}>
                Cancelar
              </Botao>
            </div>
          </div>
        </Cartao>
      )}

      {verHistorico !== null && (
        <Cartao
          titulo={`Histórico de ${verHistorico.nome}`}
          acoes={
            <Botao tipo="secundario" onClick={() => setVerHistorico(null)}>
              Fechar
            </Botao>
          }
        >
          {historico.isLoading ? (
            <Carregando />
          ) : historico.data === undefined || historico.data.historico.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum salário cadastrado ainda.</p>
          ) : (
            <Tabela
              cabecalho={
                <tr>
                  <th className="py-2 pr-3 text-right">Salário</th>
                  <th className="py-2 pr-3">De</th>
                  <th className="py-2 pr-3">Até</th>
                  <th className="py-2 pr-3">Observação</th>
                  <th className="py-2 pr-3">Cadastrado por</th>
                </tr>
              }
            >
              {historico.data.historico.map((h) => (
                <tr key={h.id}>
                  <td className="num py-2 pr-3">{reais(h.salario)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{formatarData(h.vigencia_inicio)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {h.vigencia_fim === null ? (
                      <Etiqueta tom="bom">em vigor</Etiqueta>
                    ) : (
                      formatarData(h.vigencia_fim)
                    )}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">{h.observacao ?? '—'}</td>
                  <td className="py-2 pr-3 text-slate-500">{h.criado_por ?? '—'}</td>
                </tr>
              ))}
            </Tabela>
          )}
        </Cartao>
      )}
    </div>
  );
}
